import { PrivateUser } from "../../CRUD/CampSites/types";
import { useEffect, useState } from "react";
import axios from "axios";
import { PhoneInput } from "react-international-phone";
import { PhoneNumberUtil } from 'google-libphonenumber';

interface PrivateUserFormProps {
  privateUserId?: number;
  onSuccess: () => void;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  onEdit: (privateUser: PrivateUser, isEditing: boolean) => void;
  initialName?: string;
  initialEmail?: string;
  initialPhoneNumber?: string;
  initialAddress?: string;
  initialAddress2?: string;
  initialCity?: string;
  initialState?: string;
  initialZipCode?: string;
  initialCountry?: string;
  initialComments?: string;
  isEditing?: boolean;
}

const localeMap: { [key: string]: string } = {
  "en-US": "US",
  "fr-FR": "FR",
  "sv-SE": "SE",
  sv: "SE", // Add short code for Swedish
  // Add more mappings as needed
};
const phoneUtil = PhoneNumberUtil.getInstance();
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Add email regex

const isPhoneValid = (phone: string) => {
  try {
    const parsedPhone = phoneUtil.parseAndKeepRawInput(phone);
    return phoneUtil.isValidNumber(parsedPhone);
  } catch (error) {
    console.error("Phone validation error:", error);
    return false;
  }
};

const PrivateUserForm: React.FC<PrivateUserFormProps> = ({
  privateUserId,
  onSuccess,
  onSubmit,
  onCancel,
  onEdit,
  initialName,
  initialEmail,
  initialPhoneNumber,
  initialAddress,
  initialAddress2,
  initialCity,
  initialState,
  initialZipCode,
  initialCountry,
  initialComments,
  isEditing,
}) => {
  const [name, setName] = useState(initialName || "");
  const [email, setEmail] = useState(initialEmail || "");
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || "");
  const [address, setAddress] = useState(initialAddress || "");
  const [address2, setAddress2] = useState(initialAddress2 || "");
  const [city, setCity] = useState(initialCity || "");
  const [state, setState] = useState(initialState || "");
  const [zipCode, setZipCode] = useState(initialZipCode || "");
  const [country, setCountry] = useState(initialCountry || "");
  const [comments, setComments] = useState(initialComments || "");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [phone, setPhone] = useState<string>(""); // Add phone state

  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null); // Add email error state
  const [defaultCountry, setDefaultCountry] = useState<string>("US");
  const [phoneTouched, setPhoneTouched] = useState<boolean>(false); // Track


  

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const validate = () => {
    const errors: { [key: string]: string } = {};
    if (!name) {
      errors.name = "Name is required";
    }
    if (!email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      errors.email = "Invalid email";
    }
    if (!isPhoneValid(phoneNumber)) {
      errors.phoneNumber = "Invalid phone number";
    }
    if (!address) {
      errors.address = "Address is required";
    }
    if (!city) {
      errors.city = "City is required";
    }
  
    if (!zipCode) {
      errors.zipCode = "Zip code is required";
    }
    if (!country) {
      errors.country = "Country is required";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const userLocale = navigator.language;
    const country = localeMap[userLocale] || "US";
    setDefaultCountry(country);
  }, []);

  //   useEffect(() => {
  //     if (privateUserId) {
  //         const fetchPrivateUser
  //     }
  //   }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const privateUserDTO = {
      id: privateUserId,
      name,
      email,
      phoneNumber,
      address,
      address2,
      city,
      state,
      zipCode,
      country,
      comments,
    };

    try {
      const token = localStorage.getItem("accessToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (privateUserId) {
        await axios.put(
          `${apiUrl}/PrivateUser/updatePrivateUser`,
          privateUserDTO,
          config
        );
      }
    } catch (error) {
      console.error("Error updating private user", error);
      setError("An error occurred while saving the business user.");
    }

    if (validate()) {
      onSubmit(privateUserDTO);
      onSuccess();
    }
  };

  const handlePhoneNumberChange = (value?: string) => {
    const phone = value || "";
    setPhoneNumber(phone);
    setPhone(phone);
    if (phoneTouched) {
      validatePhoneNumber(phone);
    }
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber || !isPhoneValid(phoneNumber)) {
      setPhoneNumberError("Please enter a valid phone number.");
    } else {
      setPhoneNumberError("");
    }
  };

  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    validatePhoneNumber(phoneNumber);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setEmail(email);
    validateEmail(email);
  };

  const validateEmail = (email: string) => {
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  return (
    <div className="businessUser-form-section">
        <form onSubmit={handleSubmit} className="container">
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-control"
                />
                {errors.name && <div className="text-danger">{errors.name}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    className="form-control"
                />
                {errors.email && <div className="text-danger">{errors.email}</div>}
                {emailError && <div className="text-danger">{emailError}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="phoneNumber">Phone</label>
                <PhoneInput
                style={phoneNumberError ? { border: "1px solid red" } : {}}
                name="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onBlur={handlePhoneBlur}
                defaultCountry={defaultCountry as any}
                className={`custom-phone-input ${phoneNumberError ? "error" : ""}`}
                />
                {errors.phoneNumber && <div className="text-danger">{errors.phoneNumber}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="form-control"
                />
                {errors.address && <div className="text-danger">{errors.address}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="address2">Address 2</label>
                <input
                    type="text"
                    id="address2"
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    className="form-control"
                />
            </div>
            <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                    type="text"
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="form-control"
                />
                {errors.city && <div className="text-danger">{errors.city}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                    type="text"
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="form-control"
                />
                {errors.state && <div className="text-danger">{errors.state}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="zipCode">Zip Code</label>
                <input
                    type="text"
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className="form-control"
                />
                {errors.zipCode && <div className="text-danger">{errors.zipCode}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="country">Country</label>
                <input
                    type="text"
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="form-control"
                />
                {errors.country && <div className="text-danger">{errors.country}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="comments">Comments</label>
                <textarea
                    id="comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="form-control"
                />
            </div>
            <div className="button-group">
          <button className="btn btn-outline-success" type="submit">
          {isEditing } Update Private User
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
        </form>
    </div>
  )
};

export default PrivateUserForm;
